import { useState, useEffect, useMemo, useRef } from 'react';
import {
  deleteOrgFile,
  listOrgCollection,
  writeOrgFile,
} from '../../storage/FileSystemAdapter';
import PeopleSelector from '../shared/PeopleSelector';
import EntitySelector from '../shared/EntitySelector';
import TagInput from '../shared/TagInput';
import { BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_WARN, STATUS_NEUTRAL, STATUS_INFO, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_BUTTON, FONT_LIST_ITEM, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_STAT_VALUE } from '../../theme';
import { appendChangeLog, isRecordImmutable } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { maybeAdvancePdcaStage } from '../../utils/pdcaStage';
import ConfirmDialog from '../shared/ConfirmDialog';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const ACTION_STATUSES = ['Pending', 'In Progress', 'Completed'];

const ACTION_STATUS_COLORS = {
  'Pending':     { bg: BG_MUTED, text: TEXT_MUTED },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const DECISION_CATEGORIES = ['ContinualImprovement', 'ISMSChange', 'ResourceNeeds', 'Other'];

const DECISION_STATUSES = ['Pending', 'In Progress', 'Completed'];

const DECISION_STATUS_COLORS = {
  'Pending':     { bg: BG_MUTED, text: TEXT_MUTED },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:               'Revisión por la dirección',
    newReview:           '+ Nueva revisión',
    save:                'Guardar',
    saving:              'Guardando…',
    saveOk:              '✓ Guardado',
    saveError:           'Error al guardar',
    unsavedLabel:        'Cambios sin guardar',
    unsavedConfirm:      'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:  'Salir sin guardar',
    saveAndLeave:        'Guardar y salir',
    loading:             'Cargando…',
    cntTotal:            'Total',
    cntPending:          'Acciones pendientes',
    cntLastReview:       'Última revisión',
    cntNever:            'Ninguna',
    viewBtn:             'Ver',
    editBtn:             'Editar',
    deleteBtn:           'Eliminar',
    deleteFormBtn:       'Eliminar revisión',
    deleteConfirm:       '¿Eliminar la revisión "{name}"? Esta acción no se puede deshacer.',
    selectAll:           'Seleccionar todo',
    nSelected:           '{n} seleccionados',
    deleteSelected:      'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar las siguientes revisiones?\n\n{names}\n\nEsta acción no se puede deshacer.',
    emptyTitle:          'No hay revisiones por la dirección.',
    emptyHint:           'Crea la primera revisión con el botón "+ Nueva revisión".',
    actionsPending:      'pendientes',
    actionsCompleted:    'completadas',
    nextMeeting:         'Próxima reunión:',
    formTitleNew:        'Nueva revisión por la dirección',
    formTitleEdit:       'Editar revisión por la dirección',
    formTitleView:       'Ver revisión por la dirección',
    backToList:          '← Volver al listado',
    sectionA:            'Detalles de la reunión',
    sectionB:            'Entradas (cláusula 9.3.2)',
    sectionC:            'Decisiones (salidas obligatorias, cláusula 9.3.3)',
    sectionCTooltip:     'Las salidas de la revisión por la dirección deben incluir decisiones relacionadas con oportunidades de mejora continua y necesidades de cambio en el SGSI.',
    sectionD:            'Acciones acordadas',
    fRef:                'Referencia',
    fDate:               'Fecha',
    fAttendees:          'Asistentes',
    fNextMeeting:        'Fecha de próxima reunión',
    fMinutesPath:        'Ruta del acta',
    fMinutesPathPh:      'p. ej. management-reviews/MR-2026-01-acta.docx',
    tagPlaceholder:      'Añadir y pulsar Enter…',
    decisionPlaceholder: 'Escribe una decisión y pulsa Enter para añadirla…',
    colAction:           'ACCIÓN',
    colResponsible:      'RESPONSABLE',
    colDeadline:         'FECHA LÍMITE',
    colStatus:           'ESTADO',
    colDelete:           'ELIMINAR',
    fAction:             'Acción',
    fResponsible:        'Responsable',
    fDeadline:           'Fecha límite',
    fStatus:             'Estado',
    addAction:           'Añadir',
    deleteActionConfirm: '¿Eliminar esta acción?',
    formSave:            'Guardar revisión',
    formCancel:          'Cancelar',
    validationError:     'Por favor completa los campos obligatorios marcados con *.',
    decisionsRequired:   'Debes añadir al menos una decisión (cláusula 9.3.3).',
    listColRef:          'Referencia',
    listColDate:         'Fecha',
    listColPending:      'Acciones pendientes',
    listColNextMeeting:  'Próxima reunión',
    listColActions:      'Acciones',
    lPreviousActions:    'Estado de acciones de revisiones anteriores',
    lExtIntChanges:      'Cambios en cuestiones externas e internas',
    lIntPartiesChanges:  'Cambios en necesidades de partes interesadas',
    lPerfFeedback:       'Resultados de rendimiento del SGSI',
    lNonconfTrend:       'Tendencia en no conformidades e incidentes',
    lMonitoringResults:  'Resultados de seguimiento y medición',
    lAuditResults:       'Resultados de auditorías',
    lObjFulfilment:      'Cumplimiento de objetivos de seguridad',
    lPartiesFeedback:    'Retroalimentación de partes interesadas',
    lRiskTreatment:      'Estado del tratamiento de riesgos',
    lImprovements:       'Oportunidades de mejora',
    ttPreviousActions:   'Seguimiento del estado de las acciones acordadas en revisiones por la dirección anteriores.',
    ttExtIntChanges:     'Cambios significativos en el contexto interno o externo que puedan afectar al SGSI, incluyendo requisitos legales, regulatorios o contractuales.',
    ttIntPartiesChanges: 'Cambios en las necesidades y expectativas de las partes interesadas relevantes para el SGSI.',
    ttPerfFeedback:      'Información sobre el rendimiento del SGSI, incluyendo tendencias en no conformidades, resultados de seguimiento, auditorías y cumplimiento de objetivos.',
    ttPartiesFeedback:   'Comentarios, quejas o reconocimientos recibidos de partes interesadas como clientes, proveedores o reguladores.',
    ttRiskTreatment:     'Estado actual de la evaluación y tratamiento de riesgos, incluyendo riesgos residuales aceptados y progreso del plan de tratamiento.',
    ttImprovements:      'Oportunidades identificadas para mejorar la eficacia del SGSI, incluyendo necesidades de recursos.',
    actionStatusLabels: {
      'Pending':     'Pendiente',
      'In Progress': 'En curso',
      'Completed':   'Completado',
    },
    filterPending:           'Acciones pendientes',
    filterAll:               'Todas',
    filterHasPending:        'Con acciones pendientes',
    fChairperson:            'Presidente de la reunión',
    fApprovedBy:             'Aprobado por',
    fApprovalDate:           'Fecha de aprobación',
    fTopManagementPresent:   'Alta dirección presente',
    fQuorumMet:              'Quórum alcanzado',
    fPreviousReviewId:       'ID revisión anterior',
    fClimateChange:          'Consideraciones sobre el cambio climático (AMD1:2024)',
    lResourceAdequacy:       'Adecuación de recursos',
    lCommunicationsReceived: 'Comunicaciones recibidas',
    lIncidentsSummary:       'Resumen de incidentes de seguridad',
    colDecision:             'DECISIÓN',
    colDecisionCategory:     'CATEGORÍA',
    colDecisionOwner:        'RESPONSABLE',
    colDecisionDue:          'FECHA LÍMITE',
    colDecisionStatus:       'ESTADO',
    addDecision:             'Añadir',
    fDecisionText:           'Decisión',
    fDecisionCategory:       'Categoría',
    fDecisionOwner:          'Responsable',
    fDecisionDue:            'Fecha límite',
    fDecisionStatus:         'Estado',
    deleteDecisionConfirm:   '¿Eliminar esta decisión?',
    decisionCategoryLabels: {
      ContinualImprovement: 'Mejora continua',
      ISMSChange:           'Cambio en el SGSI',
      ResourceNeeds:        'Necesidad de recursos',
      Other:                'Otro',
    },
    decisionStatusLabels: {
      'Pending':     'Pendiente',
      'In Progress': 'En curso',
      'Completed':   'Completada',
    },
    required:            '*',
    requiredLegend:      '* Campos obligatorios',
    frozenBanner:        '🔒 Este registro es de solo lectura. Para modificarlo, reabre desde Ajustes → Reabrir registros cerrados.',
    sectionE:            'Entidades revisadas',
    fLinkedAudits:       'Auditorías',
    fLinkedMetrics:      'Métricas',
    fLinkedObjectives:   'Objetivos',
    fLinkedNCs:          'No conformidades',
    fLinkedIncidents:    'Incidentes',
    fLinkedRisks:        'Riesgos',
    searchPlaceholder:   'Buscar…',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:               'Management review',
    newReview:           '+ New review',
    save:                'Save',
    saving:              'Saving…',
    saveOk:              '✓ Saved',
    saveError:           'Save error',
    unsavedLabel:        'Unsaved changes',
    unsavedConfirm:      'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:  'Leave without saving',
    saveAndLeave:        'Save and leave',
    loading:             'Loading…',
    cntTotal:            'Total',
    cntPending:          'Pending actions',
    cntLastReview:       'Last review',
    cntNever:            'None',
    viewBtn:             'View',
    editBtn:             'Edit',
    deleteBtn:           'Delete',
    deleteFormBtn:       'Delete management review',
    deleteConfirm:       'Delete review "{name}"? This cannot be undone.',
    selectAll:           'Select all',
    nSelected:           '{n} selected',
    deleteSelected:      'Delete selected',
    deleteSelectedConfirm: 'Delete the following reviews?\n\n{names}\n\nThis cannot be undone.',
    emptyTitle:          'No management reviews recorded.',
    emptyHint:           'Create the first review using the "+ New review" button.',
    actionsPending:      'pending',
    actionsCompleted:    'completed',
    nextMeeting:         'Next meeting:',
    formTitleNew:        'New management review',
    formTitleEdit:       'Edit management review',
    formTitleView:       'View management review',
    backToList:          '← Back to list',
    sectionA:            'Meeting details',
    sectionB:            'Inputs (clause 9.3.2)',
    sectionC:            'Decisions (mandatory outputs, clause 9.3.3)',
    sectionCTooltip:     'Management review outputs must include decisions related to continual improvement opportunities and any need for changes to the ISMS.',
    sectionD:            'Agreed actions',
    fRef:                'Reference',
    fDate:               'Date',
    fAttendees:          'Attendees',
    fNextMeeting:        'Next meeting date',
    fMinutesPath:        'Minutes path',
    fMinutesPathPh:      'e.g. management-reviews/MR-2026-01-minutes.docx',
    tagPlaceholder:      'Add and press Enter…',
    decisionPlaceholder: 'Type a decision and press Enter to add it…',
    colAction:           'ACTION',
    colResponsible:      'RESPONSIBLE',
    colDeadline:         'DEADLINE',
    colStatus:           'STATUS',
    colDelete:           'DELETE',
    fAction:             'Action',
    fResponsible:        'Responsible',
    fDeadline:           'Deadline',
    fStatus:             'Status',
    addAction:           'Add',
    deleteActionConfirm: 'Delete this action?',
    formSave:            'Save review',
    formCancel:          'Cancel',
    validationError:     'Please complete the required fields marked with *.',
    decisionsRequired:   'You must add at least one decision (clause 9.3.3).',
    listColRef:          'Reference',
    listColDate:         'Date',
    listColPending:      'Pending actions',
    listColNextMeeting:  'Next meeting',
    listColActions:      'Actions',
    lPreviousActions:    'Status of actions from previous reviews',
    lExtIntChanges:      'Changes in external and internal issues',
    lIntPartiesChanges:  "Changes in interested parties' needs",
    lPerfFeedback:       'ISMS performance feedback',
    lNonconfTrend:       'Nonconformities and incidents trend',
    lMonitoringResults:  'Monitoring and measurement results',
    lAuditResults:       'Audit results',
    lObjFulfilment:      'Security objectives fulfillment',
    lPartiesFeedback:    'Feedback from interested parties',
    lRiskTreatment:      'Risk treatment status',
    lImprovements:       'Improvement opportunities',
    ttPreviousActions:   'Follow-up on the status of actions agreed in previous management reviews.',
    ttExtIntChanges:     'Significant changes in internal or external context that may affect the ISMS, including legal, regulatory or contractual requirements.',
    ttIntPartiesChanges: 'Changes in the needs and expectations of interested parties relevant to the ISMS.',
    ttPerfFeedback:      'Information on ISMS performance including trends in nonconformities, monitoring results, audit results and objective fulfillment.',
    ttPartiesFeedback:   'Comments, complaints or recognition received from interested parties such as customers, suppliers or regulators.',
    ttRiskTreatment:     'Current status of risk assessment and treatment, including accepted residual risks and progress on the risk treatment plan.',
    ttImprovements:      'Identified opportunities to improve the effectiveness of the ISMS, including resource needs.',
    actionStatusLabels: {
      'Pending':     'Pending',
      'In Progress': 'In Progress',
      'Completed':   'Completed',
    },
    filterPending:           'Pending actions',
    filterAll:               'All',
    filterHasPending:        'Has pending actions',
    fChairperson:            'Chairperson',
    fApprovedBy:             'Approved by',
    fApprovalDate:           'Approval date',
    fTopManagementPresent:   'Top management present',
    fQuorumMet:              'Quorum met',
    fPreviousReviewId:       'Previous review ID',
    fClimateChange:          'Climate change considerations (AMD1:2024)',
    lResourceAdequacy:       'Adequacy of resources',
    lCommunicationsReceived: 'Communications received',
    lIncidentsSummary:       'Security incidents summary',
    colDecision:             'DECISION',
    colDecisionCategory:     'CATEGORY',
    colDecisionOwner:        'OWNER',
    colDecisionDue:          'DUE DATE',
    colDecisionStatus:       'STATUS',
    addDecision:             'Add',
    fDecisionText:           'Decision',
    fDecisionCategory:       'Category',
    fDecisionOwner:          'Owner',
    fDecisionDue:            'Due date',
    fDecisionStatus:         'Status',
    deleteDecisionConfirm:   'Delete this decision?',
    decisionCategoryLabels: {
      ContinualImprovement: 'Continual improvement',
      ISMSChange:           'ISMS change',
      ResourceNeeds:        'Resource needs',
      Other:                'Other',
    },
    decisionStatusLabels: {
      'Pending':     'Pending',
      'In Progress': 'In Progress',
      'Completed':   'Completed',
    },
    required:            '*',
    requiredLegend:      '* Required fields',
    frozenBanner:        '🔒 This record is read-only. To modify it, reopen from Settings → Reopen closed records.',
    sectionE:            'Reviewed entities',
    fLinkedAudits:       'Audits',
    fLinkedMetrics:      'Metrics',
    fLinkedObjectives:   'Objectives',
    fLinkedNCs:          'Nonconformities',
    fLinkedIncidents:    'Incidents',
    fLinkedRisks:        'Risks',
    searchPlaceholder:   'Search…',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function genId(dateStr, seq) {
  const compact = (dateStr || todayStr()).replace(/-/g, '');
  return `mr-${compact}-${String(seq).padStart(3, '0')}`;
}

function genRef(seq, mrPrefix) {
  const year = new Date().getFullYear();
  return `${mrPrefix}-${year}-${String(seq).padStart(3, '0')}`;
}

function genDecisionId() {
  return `dec-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function blankInputs() {
  return {
    previousActionsStatus:         '',
    externalInternalChanges:       '',
    interestedPartiesChanges:      '',
    performanceFeedback: {
      nonconformitiesTrend: '',
      monitoringResults:    '',
      auditResults:         '',
      objectivesFulfilment: '',
    },
    interestedPartiesFeedback:     '',
    riskTreatmentStatus:           '',
    improvementOpportunities:      '',
    climateChangeConsiderations:   '',
    resourceAdequacy:              '',
    communicationsReceived:        '',
    incidentsSummary:              '',
  };
}

function blankForm(seq, mrPrefix) {
  const today = todayStr();
  return {
    id:                    genId(today, seq),
    ref:                   genRef(seq, mrPrefix),
    date:                  today,
    attendees:             [],
    chairperson:           '',
    topManagementPresent:  false,
    quorumMet:             false,
    previousReviewId:      '',
    inputs:                blankInputs(),
    decisions:             [],
    actions:               [],
    minutesPath:           '',
    nextMeetingDate:       '',
    approvedBy:            null,
    approvalDate:          null,
    linkedAuditIds:        [],
    linkedMetricIds:       [],
    linkedObjectiveIds:    [],
    linkedNonconformityIds: [],
    linkedIncidentIds:     [],
    linkedRiskIds:         [],
    createdAt:             new Date().toISOString(),
  };
}

function mrToForm(mr) {
  return {
    id:                   mr.id,
    ref:                  mr.ref             || '',
    date:                 mr.date            || '',
    attendees:            Array.isArray(mr.attendees) ? mr.attendees : [],
    chairperson:          mr.chairperson          || '',
    topManagementPresent: !!mr.topManagementPresent,
    quorumMet:            !!mr.quorumMet,
    previousReviewId:     mr.previousReviewId     || '',
    inputs: {
      previousActionsStatus:       mr.inputs?.previousActionsStatus       || '',
      externalInternalChanges:     mr.inputs?.externalInternalChanges     || '',
      interestedPartiesChanges:    mr.inputs?.interestedPartiesChanges    || '',
      performanceFeedback: {
        nonconformitiesTrend: mr.inputs?.performanceFeedback?.nonconformitiesTrend || '',
        monitoringResults:    mr.inputs?.performanceFeedback?.monitoringResults    || '',
        auditResults:         mr.inputs?.performanceFeedback?.auditResults         || '',
        objectivesFulfilment: mr.inputs?.performanceFeedback?.objectivesFulfilment || '',
      },
      interestedPartiesFeedback:   mr.inputs?.interestedPartiesFeedback   || '',
      riskTreatmentStatus:         mr.inputs?.riskTreatmentStatus         || '',
      improvementOpportunities:    mr.inputs?.improvementOpportunities    || '',
      climateChangeConsiderations: mr.inputs?.climateChangeConsiderations || '',
      resourceAdequacy:            mr.inputs?.resourceAdequacy            || '',
      communicationsReceived:      mr.inputs?.communicationsReceived      || '',
      incidentsSummary:            mr.inputs?.incidentsSummary            || '',
    },
    // Migrate legacy string[] decisions to structured objects
    decisions: Array.isArray(mr.decisions)
      ? mr.decisions.map(d =>
          typeof d === 'string'
            ? { id: genDecisionId(), text: d, category: 'Other', ownerId: '', dueDate: '', status: 'Pending' }
            : { ...d, status: d.status === 'Open' ? 'Pending' : d.status === 'Closed' ? 'Completed' : d.status }
        )
      : [],
    actions:         Array.isArray(mr.actions) ? mr.actions : [],
    minutesPath:     mr.minutesPath     || '',
    nextMeetingDate: mr.nextMeetingDate || '',
    approvedBy:      mr.approvedBy      || null,
    approvalDate:    mr.approvalDate    || null,
    linkedAuditIds:         Array.isArray(mr.linkedAuditIds) ? mr.linkedAuditIds : [],
    linkedMetricIds:        Array.isArray(mr.linkedMetricIds) ? mr.linkedMetricIds : [],
    linkedObjectiveIds:     Array.isArray(mr.linkedObjectiveIds) ? mr.linkedObjectiveIds : [],
    linkedNonconformityIds: Array.isArray(mr.linkedNonconformityIds) ? mr.linkedNonconformityIds : [],
    linkedIncidentIds:      Array.isArray(mr.linkedIncidentIds) ? mr.linkedIncidentIds : [],
    linkedRiskIds:          Array.isArray(mr.linkedRiskIds) ? mr.linkedRiskIds : [],
    createdAt:       mr.createdAt       || new Date().toISOString(),
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL,
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL,
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const sectionBarSt = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem',
  paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};

// ── TOOLTIP ───────────────────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [vis, setVis] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      onMouseEnter={() => setVis(true)}
      onMouseLeave={() => setVis(false)}
    >
      <span style={{
        ...FONT_TINY,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%',
        border: `1px solid ${TEXT_DISABLED}`, color: TEXT_DISABLED,
        cursor: 'default', userSelect: 'none',
        fontWeight: 700, lineHeight: 1,
      }}>i</span>
      {vis && (
        <span style={{
          ...FONT_LABEL,
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a1a',
          color: SURFACE_WHITE,
          maxWidth: 280,
          width: 'max-content',
          borderRadius: 4,
          padding: '6px 10px',
          zIndex: 1000,
          lineHeight: 1.45,
          whiteSpace: 'normal',
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>{text}</span>
      )}
    </span>
  );
}

// ── SUMMARY CARD ──────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 10,
      padding: '0.9rem 1.25rem',
      flex: 1,
      minWidth: 110,
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE,
        color: color || TEXT_HEADING,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── LIST TABLE STYLES (homologated with RiskList) ────────────────────────────
const LIST_TH_STYLE = {
  ...FONT_TINY,
  color: TEXT_MUTED,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
  whiteSpace: 'nowrap',
  background: BG_SUBTLE,
};

const LIST_TD_STYLE = {
  ...FONT_LIST_ITEM,
  color: TEXT_DEFAULT,
  padding: '0.7rem 0.75rem',
  borderBottom: `1px solid ${BG_SHELL}`,
  verticalAlign: 'middle',
};

// ── MR ROW ────────────────────────────────────────────────────────────────────
function MRRow({ mr, lang, onEdit, onView, onDelete, checked, onToggle }) {
  const t = T[lang] || T.en;
  const actions = Array.isArray(mr.actions) ? mr.actions : [];
  const pending = actions.filter(a => a.status !== 'Completed').length;

  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_SMALL,
    borderRadius: 6, padding: '0.25rem 0.7rem', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  return (
    <tr>
      <td style={{ ...LIST_TD_STYLE, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onToggle(mr.id, e.target.checked)} />
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, fontWeight: 500, color: TEXT_HEADING, whiteSpace: 'nowrap' }}>
        {mr.ref}
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, whiteSpace: 'nowrap' }}>
        {mr.date || '—'}
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, whiteSpace: 'nowrap', color: pending > 0 ? STATUS_WARN.text : TEXT_MUTED }}>
        {pending}
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, whiteSpace: 'nowrap' }}>
        {mr.nextMeetingDate || '—'}
      </td>
      <td style={{ ...LIST_TD_STYLE, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <button onClick={() => onView(mr)} style={btnSmall}>{t.viewBtn}</button>
          <button onClick={() => onEdit(mr)} style={btnSmall}>{t.editBtn}</button>
          <button onClick={() => onDelete(mr)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteBtn}</button>
        </div>
      </td>
    </tr>
  );
}

// ── MANAGEMENT REVIEW FORM ────────────────────────────────────────────────────
function ManagementReviewForm({ t, lang, mode, mrId, initialValues, onSave, onCancel, onDelete, org, saving: parentSaving, saveError, onFormChange, saveRequestTick, dirty }) {
  const isView = mode === 'view';
  // §11 rule 34 — read-only banner shown only when the form is in view mode
  // because the review is in a terminal/immutable state (approvalDate set),
  // not when the user voluntarily opened a non-immutable review in view mode.
  const frozen = isView && isRecordImmutable({ approvalDate: initialValues.approvalDate }, 'managementReview');
  const [values, setValues]       = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [newAction, setNewAction] = useState({ action: '', responsible: '', deadline: '', status: 'Pending' });
  const [newActionErr, setNewActionErr] = useState(false);
  const [newDecision, setNewDecision] = useState({ text: '', category: 'ContinualImprovement', ownerId: '', dueDate: '', status: 'Pending' });
  const [newDecisionErr, setNewDecisionErr] = useState(false);
  const [auditOptions,     setAuditOptions]     = useState([]);
  const [metricOptions,    setMetricOptions]    = useState([]);
  const [objectiveOptions, setObjectiveOptions] = useState([]);
  const [ncOptions,        setNcOptions]        = useState([]);
  const [incidentOptions,  setIncidentOptions]  = useState([]);
  const [riskOptions,      setRiskOptions]      = useState([]);
  const [saving, setSaving] = useState(false);
  const savingAny = saving || parentSaving;
  const [confirmState, setConfirmState] = useState(null);
  const hasChangedRef = useRef(false);

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const set = (field, val) => {
    markChange();
    setValues(v => ({ ...v, [field]: val }));
  };

  const setInputField = (field, val) => {
    markChange();
    setValues(v => ({ ...v, inputs: { ...v.inputs, [field]: val } }));
  };

  const setPerfField = (field, val) => {
    markChange();
    setValues(v => ({
      ...v,
      inputs: {
        ...v.inputs,
        performanceFeedback: { ...v.inputs.performanceFeedback, [field]: val },
      },
    }));
  };

  const setNA = (field, val) => setNewAction(a => ({ ...a, [field]: val }));
  const setND = (field, val) => setNewDecision(d => ({ ...d, [field]: val }));

  const handleAddDecision = () => {
    if (!newDecision.text.trim()) {
      setNewDecisionErr(true);
      return;
    }
    setNewDecisionErr(false);
    set('decisions', [...values.decisions, { ...newDecision, id: genDecisionId(), text: newDecision.text.trim() }]);
    setNewDecision({ text: '', category: 'ContinualImprovement', ownerId: '', dueDate: '', status: 'Pending' });
  };

  const handleDeleteDecision = (idx) => {
    setConfirmState({
      message: t.deleteDecisionConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('decisions', values.decisions.filter((_, i) => i !== idx));
      },
    });
  };

  const handleDecisionStatusChange = (idx, status) => {
    const updated = [...values.decisions];
    updated[idx] = { ...updated[idx], status };
    set('decisions', updated);
  };

  const handleDecisionOwnerChange = (idx, ownerId) => {
    const updated = [...values.decisions];
    updated[idx] = { ...updated[idx], ownerId };
    set('decisions', updated);
  };

  const handleAddAction = () => {
    if (!newAction.action.trim() || !newAction.responsible.trim() || !newAction.deadline) {
      setNewActionErr(true);
      return;
    }
    setNewActionErr(false);
    set('actions', [...values.actions, { ...newAction, action: newAction.action.trim(), responsible: newAction.responsible.trim() }]);
    setNewAction({ action: '', responsible: '', deadline: '', status: 'Pending' });
  };

  const handleDeleteAction = (idx) => {
    setConfirmState({
      message: t.deleteActionConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('actions', values.actions.filter((_, i) => i !== idx));
      },
    });
  };

  const handleActionStatusChange = (idx, status) => {
    const updated = [...values.actions];
    updated[idx] = { ...updated[idx], status };
    set('actions', updated);
  };

  const handleActionResponsibleChange = (idx, name) => {
    const updated = [...values.actions];
    updated[idx] = { ...updated[idx], responsible: name };
    set('actions', updated);
  };

  const handleSubmit = () => {
    const errs = {};
    if (!values.ref.trim())                                   errs.ref = true;
    if (!values.date)                                         errs.date = true;
    if (!values.attendees.length)                             errs.attendees = true;
    if (!values.inputs.previousActionsStatus.trim())          errs.previousActionsStatus = true;
    if (!values.inputs.externalInternalChanges.trim())        errs.externalInternalChanges = true;
    if (!values.inputs.interestedPartiesChanges.trim())       errs.interestedPartiesChanges = true;
    if (!values.inputs.interestedPartiesFeedback.trim())      errs.interestedPartiesFeedback = true;
    if (!values.inputs.riskTreatmentStatus.trim())            errs.riskTreatmentStatus = true;
    if (!values.inputs.improvementOpportunities.trim())       errs.improvementOpportunities = true;
    if (!Array.isArray(values.decisions) || values.decisions.length === 0) errs.decisions = true;
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    onSave(values);
  };

  useEffect(() => {
    Promise.all([
      listOrgCollection(org.id, 'audits')
        .then(items => setAuditOptions(items.map(a => ({ id: a.id, label: a.ref || a.id }))))
        .catch(() => setAuditOptions([])),
      listOrgCollection(org.id, 'metrics')
        .then(items => setMetricOptions(items.map(m => ({ id: m.id, label: (m.ref ? m.ref + ' — ' : '') + (m.name || m.id) }))))
        .catch(() => setMetricOptions([])),
      listOrgCollection(org.id, 'objectives')
        .then(items => setObjectiveOptions(items.map(o => ({ id: o.id, label: (o.ref ? o.ref + ' — ' : '') + (o.objective || o.id) }))))
        .catch(() => setObjectiveOptions([])),
      listOrgCollection(org.id, 'nonconformities')
        .then(items => setNcOptions(items.map(nc => ({ id: nc.id, label: nc.ref || nc.id }))))
        .catch(() => setNcOptions([])),
      listOrgCollection(org.id, 'incidents')
        .then(items => setIncidentOptions(items.map(i => ({ id: i.id, label: i.title || i.ref || i.id }))))
        .catch(() => setIncidentOptions([])),
      listOrgCollection(org.id, 'risks')
        .then(items => setRiskOptions(items.map(r => ({ id: r.id, label: (r.ref ? r.ref + ' — ' : '') + (r.riskSummary || r.id) }))))
        .catch(() => setRiskOptions([])),
    ]);
  }, [org.id]);

  useEffect(() => {
    if (saveRequestTick > 0) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const fWrap   = { display: 'flex', flexDirection: 'column', gap: '0.15rem' };
  const req     = <span style={{ color: ERROR_TEXT }}>{t.required}</span>;

  // Input block style (section B accent)
  const inputBlock = (hasErr) => ({
    borderLeft: `3px solid ${hasErr ? FIELD_ERROR_BORDER : BRAND_PRIMARY}`,
    paddingLeft: '0.85rem',
    marginBottom: '0.9rem',
  });

  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 12,
      padding: '1.75rem 2rem',
      marginTop: '1rem',
      position: 'relative',
    }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          ...FONT_LABEL,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: BRAND_PRIMARY, marginBottom: '0.5rem', display: 'block',
        }}
      >
        {t.backToList}
      </button>
      <h3 style={{
        ...FONT_SECTION_TITLE,
        color: TEXT_HEADING, margin: '0 0 1.5rem',
      }}>
        {isView
          ? t.formTitleView
          : (initialValues.id && initialValues.ref && initialValues.date
              ? t.formTitleEdit
              : t.formTitleNew)}
      </h3>
      {frozen && (
        <div style={{
          background: STATUS_NEUTRAL.bg, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
          ...FONT_LABEL, color: TEXT_DEFAULT,
        }}>
          {t.frozenBanner}
        </div>
      )}
      {!isView && (
        <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
      )}
      <fieldset disabled={isView || savingAny} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── SECTION A ──────────────────────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionA}</div>

      {/* Ref + Date */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fRef} {req}</label>
          <input
            type="text"
            value={values.ref}
            onChange={e => set('ref', e.target.value)}
            style={inputSt(formErrors.ref)}
          />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fDate} {req}</label>
          <input
            type="date"
            value={values.date}
            onChange={e => set('date', e.target.value)}
            style={inputSt(formErrors.date)}
          />
        </div>
      </div>

      {/* Attendees */}
      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fAttendees} {req}</label>
        <PeopleSelector
          orgId={org.id}
          lang={lang}
          multiple
          value={values.attendees}
          onChange={(v) => set('attendees', v)}
        />
      </div>

      {/* Chairperson */}
      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fChairperson}</label>
        <PeopleSelector
          orgId={org.id}
          lang={lang}
          value={values.chairperson}
          onChange={(v) => set('chairperson', v)}
        />
      </div>

      {/* topManagementPresent + quorumMet + previousReviewId */}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '0.9rem', alignItems: 'center', marginBottom: '0.9rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            id="topManagementPresent"
            checked={values.topManagementPresent}
            onChange={e => set('topManagementPresent', e.target.checked)}
          />
          <label htmlFor="topManagementPresent" style={{ ...labelSt, marginBottom: 0, cursor: 'pointer' }}>{t.fTopManagementPresent}</label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            id="quorumMet"
            checked={values.quorumMet}
            onChange={e => set('quorumMet', e.target.checked)}
          />
          <label htmlFor="quorumMet" style={{ ...labelSt, marginBottom: 0, cursor: 'pointer' }}>{t.fQuorumMet}</label>
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fPreviousReviewId}</label>
          <input
            type="text"
            value={values.previousReviewId}
            onChange={e => set('previousReviewId', e.target.value)}
            style={inputSt(false)}
          />
        </div>
      </div>

      {/* Approved by + Approval date */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fApprovedBy}</label>
          <PeopleSelector
            orgId={org.id}
            lang={lang}
            value={values.approvedBy}
            onChange={(v) => set('approvedBy', v)}
          />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fApprovalDate}</label>
          <input
            type="date"
            value={values.approvalDate || ''}
            onChange={e => set('approvalDate', e.target.value || null)}
            style={inputSt(false)}
          />
        </div>
      </div>

      {/* Next meeting date + Minutes path */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '1.5rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fNextMeeting}</label>
          <input
            type="date"
            value={values.nextMeetingDate}
            onChange={e => set('nextMeetingDate', e.target.value)}
            style={inputSt(false)}
          />
        </div>
        <div style={fWrap}>
          <label style={labelSt}>{t.fMinutesPath}</label>
          <input
            type="text"
            value={values.minutesPath}
            onChange={e => set('minutesPath', e.target.value)}
            placeholder={t.fMinutesPathPh}
            style={inputSt(false)}
          />
        </div>
      </div>

      {/* ── SECTION B ──────────────────────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionB}</div>

      {/* 1. previousActionsStatus */}
      <div style={inputBlock(formErrors.previousActionsStatus)}>
        <div style={{ ...fWrap, marginBottom: 0 }}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lPreviousActions} {req}
              <Tooltip text={t.ttPreviousActions} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.previousActionsStatus}
            onChange={e => setInputField('previousActionsStatus', e.target.value)}
            style={{ ...inputSt(formErrors.previousActionsStatus), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 2. externalInternalChanges */}
      <div style={inputBlock(formErrors.externalInternalChanges)}>
        <div style={fWrap}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lExtIntChanges} {req}
              <Tooltip text={t.ttExtIntChanges} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.externalInternalChanges}
            onChange={e => setInputField('externalInternalChanges', e.target.value)}
            style={{ ...inputSt(formErrors.externalInternalChanges), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 3. interestedPartiesChanges */}
      <div style={inputBlock(formErrors.interestedPartiesChanges)}>
        <div style={fWrap}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lIntPartiesChanges} {req}
              <Tooltip text={t.ttIntPartiesChanges} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.interestedPartiesChanges}
            onChange={e => setInputField('interestedPartiesChanges', e.target.value)}
            style={{ ...inputSt(formErrors.interestedPartiesChanges), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 4. performanceFeedback group */}
      <div style={inputBlock(false)}>
        <div style={{
          ...FONT_SMALL,
          color: TEXT_MUTED, letterSpacing: '0.03em',
          textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          marginBottom: '0.65rem',
        }}>
          {t.lPerfFeedback}
          <Tooltip text={t.ttPerfFeedback} />
        </div>
        {/* Sub-fields */}
        {[
          { key: 'nonconformitiesTrend', label: t.lNonconfTrend },
          { key: 'monitoringResults',    label: t.lMonitoringResults },
          { key: 'auditResults',         label: t.lAuditResults },
          { key: 'objectivesFulfilment', label: t.lObjFulfilment },
        ].map(({ key, label }) => (
          <div key={key} style={{ ...fWrap, marginBottom: '0.65rem', paddingLeft: 16 }}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>{label}</label>
            <textarea
              rows={2}
              value={values.inputs.performanceFeedback[key]}
              onChange={e => setPerfField(key, e.target.value)}
              style={{ ...inputSt(false), resize: 'vertical', fontSize: '0.82rem' }}
            />
          </div>
        ))}
      </div>

      {/* 5. interestedPartiesFeedback */}
      <div style={inputBlock(formErrors.interestedPartiesFeedback)}>
        <div style={fWrap}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lPartiesFeedback} {req}
              <Tooltip text={t.ttPartiesFeedback} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.interestedPartiesFeedback}
            onChange={e => setInputField('interestedPartiesFeedback', e.target.value)}
            style={{ ...inputSt(formErrors.interestedPartiesFeedback), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 6. riskTreatmentStatus */}
      <div style={inputBlock(formErrors.riskTreatmentStatus)}>
        <div style={fWrap}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lRiskTreatment} {req}
              <Tooltip text={t.ttRiskTreatment} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.riskTreatmentStatus}
            onChange={e => setInputField('riskTreatmentStatus', e.target.value)}
            style={{ ...inputSt(formErrors.riskTreatmentStatus), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 7. improvementOpportunities */}
      <div style={inputBlock(formErrors.improvementOpportunities)}>
        <div style={fWrap}>
          <label style={labelSt}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {t.lImprovements} {req}
              <Tooltip text={t.ttImprovements} />
            </span>
          </label>
          <textarea
            rows={3}
            value={values.inputs.improvementOpportunities}
            onChange={e => setInputField('improvementOpportunities', e.target.value)}
            style={{ ...inputSt(formErrors.improvementOpportunities), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 8. climateChangeConsiderations (AMD1:2024) */}
      <div style={inputBlock(false)}>
        <div style={fWrap}>
          <label style={labelSt}>{t.fClimateChange}</label>
          <textarea
            rows={3}
            value={values.inputs.climateChangeConsiderations}
            onChange={e => setInputField('climateChangeConsiderations', e.target.value)}
            style={{ ...inputSt(false), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 9. resourceAdequacy */}
      <div style={inputBlock(false)}>
        <div style={fWrap}>
          <label style={labelSt}>{t.lResourceAdequacy}</label>
          <textarea
            rows={3}
            value={values.inputs.resourceAdequacy}
            onChange={e => setInputField('resourceAdequacy', e.target.value)}
            style={{ ...inputSt(false), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 10. communicationsReceived */}
      <div style={inputBlock(false)}>
        <div style={fWrap}>
          <label style={labelSt}>{t.lCommunicationsReceived}</label>
          <textarea
            rows={3}
            value={values.inputs.communicationsReceived}
            onChange={e => setInputField('communicationsReceived', e.target.value)}
            style={{ ...inputSt(false), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* 11. incidentsSummary */}
      <div style={{ ...inputBlock(false), marginBottom: '1.5rem' }}>
        <div style={fWrap}>
          <label style={labelSt}>{t.lIncidentsSummary}</label>
          <textarea
            rows={3}
            value={values.inputs.incidentsSummary}
            onChange={e => setInputField('incidentsSummary', e.target.value)}
            style={{ ...inputSt(false), resize: 'vertical' }}
          />
        </div>
      </div>

      {/* ── SECTION C — Decisions ───────────────────────────────────────────── */}
      <div style={sectionBarSt}>
        {t.sectionC} {req}
        <Tooltip text={t.sectionCTooltip} />
      </div>
      {formErrors.decisions && (
        <div style={{
          ...FONT_LABEL,
          color: ERROR_TEXT, marginBottom: '0.5rem',
        }}>
          {t.decisionsRequired}
        </div>
      )}
      <div style={{ marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t.colDecision, t.colDecisionCategory, t.colDecisionOwner, t.colDecisionDue, t.colDecisionStatus, t.colDelete].map(col => (
                <th key={col} style={{
                  ...FONT_TINY,
                  color: TEXT_MUTED, letterSpacing: '0.05em',
                  textAlign: 'left', padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {values.decisions.map((dec, idx) => {
              const sc = DECISION_STATUS_COLORS[dec.status] || DECISION_STATUS_COLORS['Pending'];
              return (
                <tr key={dec.id || idx} style={{ borderBottom: `1px solid ${BG_MUTED}`, border: formErrors.decisions ? `1px solid ${ERROR_BORDER}` : undefined }}>
                  <td style={{ ...FONT_LABEL, padding: '0.45rem 0.5rem', color: TEXT_DEFAULT, wordBreak: 'break-word' }}>
                    {dec.text}
                  </td>
                  <td style={{ ...FONT_SMALL, padding: '0.45rem 0.5rem', color: TEXT_DEFAULT }}>
                    {t.decisionCategoryLabels[dec.category] || dec.category}
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem' }}>
                    <PeopleSelector
                      orgId={org.id}
                      lang={lang}
                      value={dec.ownerId}
                      onChange={(v) => handleDecisionOwnerChange(idx, v)}
                      disabled={saving}
                    />
                  </td>
                  <td style={{ ...FONT_LABEL, padding: '0.45rem 0.5rem', color: TEXT_DEFAULT }}>
                    {dec.dueDate || '—'}
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem' }}>
                    <select
                      value={dec.status}
                      onChange={e => handleDecisionStatusChange(idx, e.target.value)}
                      style={{
                        ...FONT_SMALL,
                        background: sc.bg, color: sc.text,
                        border: 'none', borderRadius: 4,
                        fontWeight: 600, padding: '0.2rem 0.4rem',
                        cursor: 'pointer', outline: 'none',
                        width: '100%',
                      }}
                    >
                      {DECISION_STATUSES.map(s => <option key={s} value={s}>{t.decisionStatusLabels[s]}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => handleDeleteDecision(idx)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: TEXT_DISABLED, fontSize: '0.9rem', lineHeight: 1, padding: 0,
                      }}
                    >✕</button>
                  </td>
                </tr>
              );
            })}

            {/* Add-entry row */}
            <tr style={{ background: BG_SURFACE, borderTop: `2px solid ${STATUS_NEUTRAL.bg}` }}>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input
                  type="text"
                  value={newDecision.text}
                  onChange={e => setND('text', e.target.value)}
                  placeholder={t.fDecisionText}
                  style={{
                    ...inputSt(newDecisionErr && !newDecision.text.trim()),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                  }}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <select
                  value={newDecision.category}
                  onChange={e => setND('category', e.target.value)}
                  style={{
                    ...inputSt(false),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                    width: '100%',
                  }}
                >
                  {DECISION_CATEGORIES.map(c => (
                    <option key={c} value={c}>{t.decisionCategoryLabels[c]}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top' }}>
                <PeopleSelector
                  orgId={org.id}
                  lang={lang}
                  value={newDecision.ownerId}
                  onChange={(v) => setNewDecision(d => ({ ...d, ownerId: v }))}
                  disabled={saving}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input
                  type="date"
                  value={newDecision.dueDate}
                  onChange={e => setND('dueDate', e.target.value)}
                  style={{
                    ...inputSt(false),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                  }}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <select
                  value={newDecision.status}
                  onChange={e => setND('status', e.target.value)}
                  style={{
                    ...inputSt(false),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                    width: '100%',
                  }}
                >
                  {DECISION_STATUSES.map(s => <option key={s} value={s}>{t.decisionStatusLabels[s]}</option>)}
                </select>
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleAddDecision}
                  style={{
                    ...FONT_SMALL,
                    background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                    borderRadius: 6, padding: '0.3rem 0.6rem', cursor: 'pointer',
                  }}
                >
                  {t.addDecision}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── SECTION D — Actions ─────────────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionD}</div>

      {/* Actions table */}
      <div style={{ marginBottom: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[t.colAction, t.colResponsible, t.colDeadline, t.colStatus, t.colDelete].map(col => (
                <th key={col} style={{
                  ...FONT_TINY,
                  color: TEXT_MUTED, letterSpacing: '0.05em',
                  textAlign: 'left', padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {values.actions.map((action, idx) => {
              const sc = ACTION_STATUS_COLORS[action.status] || ACTION_STATUS_COLORS['Pending'];
              return (
                <tr key={idx} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                  <td style={{ ...FONT_LABEL, padding: '0.45rem 0.5rem', color: TEXT_DEFAULT, wordBreak: 'break-word' }}>
                    {action.action}
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem' }}>
                    <PeopleSelector
                      orgId={org.id}
                      lang={lang}
                      value={action.responsible}
                      onChange={(v) => handleActionResponsibleChange(idx, v)}
                      disabled={saving}
                    />
                  </td>
                  <td style={{ ...FONT_LABEL, padding: '0.45rem 0.5rem', color: TEXT_DEFAULT }}>
                    {action.deadline}
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem' }}>
                    <select
                      value={action.status}
                      onChange={e => handleActionStatusChange(idx, e.target.value)}
                      style={{
                        ...FONT_SMALL,
                        background: sc.bg, color: sc.text,
                        border: 'none', borderRadius: 4,
                        fontWeight: 600, padding: '0.2rem 0.4rem',
                        cursor: 'pointer', outline: 'none',
                        width: '100%',
                      }}
                    >
                      {ACTION_STATUSES.map(s => <option key={s} value={s}>{t.actionStatusLabels[s]}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => handleDeleteAction(idx)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: TEXT_DISABLED, fontSize: '0.9rem', lineHeight: 1, padding: 0,
                      }}
                    >✕</button>
                  </td>
                </tr>
              );
            })}

            {/* Add-entry row */}
            <tr style={{ background: BG_SURFACE, borderTop: `2px solid ${STATUS_NEUTRAL.bg}` }}>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input
                  type="text"
                  value={newAction.action}
                  onChange={e => setNA('action', e.target.value)}
                  placeholder={t.fAction}
                  style={{
                    ...inputSt(newActionErr && !newAction.action.trim()),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                  }}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top' }}>
                <PeopleSelector
                  orgId={org.id}
                  lang={lang}
                  value={newAction.responsible}
                  onChange={(v) => setNewAction(a => ({ ...a, responsible: v }))}
                  disabled={saving}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <input
                  type="date"
                  value={newAction.deadline}
                  onChange={e => setNA('deadline', e.target.value)}
                  style={{
                    ...inputSt(newActionErr && !newAction.deadline),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                  }}
                />
              </td>
              <td style={{ padding: '0.4rem 0.5rem' }}>
                <select
                  value={newAction.status}
                  onChange={e => setNA('status', e.target.value)}
                  style={{
                    ...inputSt(false),
                    padding: '0.3rem 0.5rem', fontSize: '0.78rem',
                    width: '100%',
                  }}
                >
                  {ACTION_STATUSES.map(s => <option key={s} value={s}>{t.actionStatusLabels[s]}</option>)}
                </select>
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleAddAction}
                  style={{
                    ...FONT_SMALL,
                    background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                    borderRadius: 6, padding: '0.3rem 0.6rem', cursor: 'pointer',
                  }}
                >
                  {t.addAction}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── SECTION E — Relationships ───────────────────────────────────────── */}
      <div style={sectionBarSt}>{t.sectionE}</div>

      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedAudits}</label>
        {auditOptions.length > 0 ? (
          <EntitySelector
            items={auditOptions}
            selectedIds={values.linkedAuditIds}
            onAdd={(id) => set('linkedAuditIds', [...values.linkedAuditIds, id])}
            onRemove={(id) => set('linkedAuditIds', values.linkedAuditIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedAuditIds}
            onAdd={(v) => set('linkedAuditIds', [...values.linkedAuditIds, v])}
            onRemove={(v) => set('linkedAuditIds', values.linkedAuditIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedAuditIds', values.linkedAuditIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedMetrics}</label>
        {metricOptions.length > 0 ? (
          <EntitySelector
            items={metricOptions}
            selectedIds={values.linkedMetricIds}
            onAdd={(id) => set('linkedMetricIds', [...values.linkedMetricIds, id])}
            onRemove={(id) => set('linkedMetricIds', values.linkedMetricIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedMetricIds}
            onAdd={(v) => set('linkedMetricIds', [...values.linkedMetricIds, v])}
            onRemove={(v) => set('linkedMetricIds', values.linkedMetricIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedMetricIds', values.linkedMetricIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedObjectives}</label>
        {objectiveOptions.length > 0 ? (
          <EntitySelector
            items={objectiveOptions}
            selectedIds={values.linkedObjectiveIds}
            onAdd={(id) => set('linkedObjectiveIds', [...values.linkedObjectiveIds, id])}
            onRemove={(id) => set('linkedObjectiveIds', values.linkedObjectiveIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedObjectiveIds}
            onAdd={(v) => set('linkedObjectiveIds', [...values.linkedObjectiveIds, v])}
            onRemove={(v) => set('linkedObjectiveIds', values.linkedObjectiveIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedObjectiveIds', values.linkedObjectiveIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedNCs}</label>
        {ncOptions.length > 0 ? (
          <EntitySelector
            items={ncOptions}
            selectedIds={values.linkedNonconformityIds}
            onAdd={(id) => set('linkedNonconformityIds', [...values.linkedNonconformityIds, id])}
            onRemove={(id) => set('linkedNonconformityIds', values.linkedNonconformityIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedNonconformityIds}
            onAdd={(v) => set('linkedNonconformityIds', [...values.linkedNonconformityIds, v])}
            onRemove={(v) => set('linkedNonconformityIds', values.linkedNonconformityIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedNonconformityIds', values.linkedNonconformityIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      <div style={{ ...fWrap, marginBottom: '0.9rem' }}>
        <label style={labelSt}>{t.fLinkedIncidents}</label>
        {incidentOptions.length > 0 ? (
          <EntitySelector
            items={incidentOptions}
            selectedIds={values.linkedIncidentIds}
            onAdd={(id) => set('linkedIncidentIds', [...values.linkedIncidentIds, id])}
            onRemove={(id) => set('linkedIncidentIds', values.linkedIncidentIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedIncidentIds}
            onAdd={(v) => set('linkedIncidentIds', [...values.linkedIncidentIds, v])}
            onRemove={(v) => set('linkedIncidentIds', values.linkedIncidentIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedIncidentIds', values.linkedIncidentIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      <div style={{ ...fWrap, marginBottom: '1.5rem' }}>
        <label style={labelSt}>{t.fLinkedRisks}</label>
        {riskOptions.length > 0 ? (
          <EntitySelector
            items={riskOptions}
            selectedIds={values.linkedRiskIds}
            onAdd={(id) => set('linkedRiskIds', [...values.linkedRiskIds, id])}
            onRemove={(id) => set('linkedRiskIds', values.linkedRiskIds.filter(x => x !== id))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
            chipBg={STATUS_INFO.bg}
            chipColor="#1d4ed8"
          />
        ) : (
          <TagInput
            tags={values.linkedRiskIds}
            onAdd={(v) => set('linkedRiskIds', [...values.linkedRiskIds, v])}
            onRemove={(v) => set('linkedRiskIds', values.linkedRiskIds.filter(x => x !== v))}
            onUpdate={(oldValue, newValue) => set('linkedRiskIds', values.linkedRiskIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.searchPlaceholder}
            disabled={isView || savingAny}
          />
        )}
      </div>

      </fieldset>

      {/* Sticky action bar */}
      {!isView && (
      <div style={{
        position: 'sticky',
        bottom: 0,
        marginLeft: '-2rem',
        marginRight: '-2rem',
        marginBottom: '-1.75rem',
        marginTop: '1.5rem',
        padding: '0.85rem 2rem',
        background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        zIndex: 10,
      }}>
      {Object.keys(formErrors).length > 0 && (
        <div style={{
          ...FONT_LABEL,
          color: ERROR_TEXT, marginBottom: '0.5rem',
        }}>
          {t.validationError}
        </div>
      )}

      {saveError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 6, padding: '0.35rem 0.75rem',
          marginBottom: '0.5rem',
        }}>{t.saveError}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          {mrId && mrId !== 'new' && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={parentSaving}
              style={{
                ...BTN_DESTRUCTIVE_OUTLINE,
                ...FONT_LABEL,
                borderRadius: 8,
                padding: '0.5rem 1rem',
                cursor: parentSaving ? 'not-allowed' : 'pointer',
              }}
            >{t.deleteFormBtn}</button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {dirty && (
            <span style={{
              ...FONT_LABEL,
              color: UNSAVED_AMBER,
            }}>{t.unsavedLabel}</span>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={parentSaving}
            style={{
              ...BTN_SECONDARY, ...FONT_BUTTON, fontWeight: 400, borderRadius: 8,
              padding: '0.6rem 1.25rem', cursor: parentSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {t.formCancel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={parentSaving}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, fontWeight: 700,
              background: parentSaving ? TEXT_MUTED : BTN_PRIMARY.background, border: 'none',
              borderRadius: 8, padding: '0.6rem 1.6rem', cursor: parentSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {parentSaving ? t.saving : t.formSave}
          </button>
        </div>
      </div>
      </div>
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
      />
    </div>
  );
}

// ── MANAGEMENT REVIEWS VIEW ───────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function ManagementReviewsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [allReviews, setAllReviews] = useState(null);   // null = loading
  const [editingId,  setEditingId]  = useState(null);   // null | 'new' | mr.id
  const [formMode,   setFormMode]   = useState('edit'); // 'create' | 'edit' | 'view'
  const [formInitial, setFormInitial] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saveState, setSaveState] = useState('idle');
  const [saving, setSaving]       = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);
  const [dirty, setDirty]               = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef               = useRef(false);
  const lastProcessedAppTickRef         = useRef(0);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterPending, setFilterPending] = useState(''); // '' = all, 'pending' = only with pending actions

  useScrollToTopOnFormOpen(editingId !== null);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'management-reviews')
      .then(items => setAllReviews(items))
      .catch(() => setAllReviews([]));
  }, [org.id]);

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (editingId !== null && formInitial !== null) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── New ──────────────────────────────────────────────────────────────────
  const handleNew = async () => {
    const mrPrefix = (org.refPrefixes && org.refPrefixes.managementReview) || 'mrv';
    let seq;
    try {
      const items = await listOrgCollection(org.id, 'management-reviews');
      const year = new Date().getFullYear();
      const prefix = `${mrPrefix}-${year}-`;
      let max = 0;
      items.forEach(r => {
        if (r.ref && r.ref.startsWith(prefix)) {
          const n = parseInt(r.ref.slice(prefix.length), 10);
          if (!isNaN(n) && n > max) max = n;
        }
      });
      seq = max + 1;
    } catch {
      const ts = Date.now().toString().slice(-4);
      seq = parseInt(ts, 10);
    }
    setFormInitial(blankForm(seq, mrPrefix));
    setFormMode('create');
    setEditingId('new');
    setDirty(false);
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const handleEdit = (mr) => {
    setFormInitial(mrToForm(mr));
    // §11 rule 34 — MR with approvalDate set is immutable; open read-only.
    setFormMode(isRecordImmutable(mr, 'managementReview') ? 'view' : 'edit');
    setEditingId(mr.id);
    setDirty(false);
  };

  // ── View ─────────────────────────────────────────────────────────────────
  const handleView = (mr) => {
    setFormInitial(mrToForm(mr));
    setFormMode('view');
    setEditingId(mr.id);
  };

  // ── Cancel ───────────────────────────────────────────────────────────────
  const closeForm = () => {
    setDirty(false);
    setEditingId(null);
    setFormInitial(null);
  };

  const handleCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── Form save → update local state, queue write ──────────────────────────
  const handleFormSave = async (values) => {
    // §11 rule 34 — immutability: a ManagementReview with approvalDate set
    // is frozen. Only the Settings reopen flow can clear approvalDate and
    // return it to an editable state.
    const existingMr = (allReviews || []).find(r => r.id === values.id);
    if (existingMr && isRecordImmutable(existingMr, 'managementReview')) {
      setSaving(false);
      return;
    }
    setSaving(true);
    const changedBy = org.consultantPersonId || 'system';
    const mr = {
      id:                   values.id,
      orgId:                org.id,
      ref:                  values.ref.trim(),
      date:                 values.date,
      attendees:            values.attendees,
      chairperson:          values.chairperson         || null,
      topManagementPresent: !!values.topManagementPresent,
      quorumMet:            !!values.quorumMet,
      previousReviewId:     values.previousReviewId?.trim() || null,
      inputs:               values.inputs,
      decisions:            values.decisions,
      actions:              values.actions,
      minutesPath:          values.minutesPath.trim()  || null,
      nextMeetingDate:      values.nextMeetingDate     || null,
      approvedBy:           values.approvedBy          || null,
      approvalDate:         values.approvalDate        || null,
      linkedAuditIds:         Array.isArray(values.linkedAuditIds) ? values.linkedAuditIds : [],
      linkedMetricIds:        Array.isArray(values.linkedMetricIds) ? values.linkedMetricIds : [],
      linkedObjectiveIds:     Array.isArray(values.linkedObjectiveIds) ? values.linkedObjectiveIds : [],
      linkedNonconformityIds: Array.isArray(values.linkedNonconformityIds) ? values.linkedNonconformityIds : [],
      linkedIncidentIds:      Array.isArray(values.linkedIncidentIds) ? values.linkedIncidentIds : [],
      linkedRiskIds:          Array.isArray(values.linkedRiskIds) ? values.linkedRiskIds : [],
      createdAt:            values.createdAt,
      updatedAt:            new Date().toISOString(),
    };
    mr.changeLog = existingMr ? appendChangeLog(existingMr, mr, changedBy) : [];

    try {
      await writeOrgFile(org.id, `management-reviews/${mr.id}.json`, mr);
      setAllReviews(prev => {
        const list = prev || [];
        const idx = list.findIndex(r => r.id === mr.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = mr;
          return updated;
        }
        return [...list, mr];
      });
      setSaving(false);
      if (editingId === 'new') {
        setEditingId(mr.id);
        setFormMode('edit');
      }
      setDirty(false);
      setSaveRequestTick(0);
      // §4 pdcaStage auto-advance — a newly saved MR carrying ≥ 1 decision
      // is the Check → Act trigger. Evaluated best-effort; App.js syncs the
      // org's pdcaStage on its next load.
      maybeAdvancePdcaStage(org.id, org).catch(() => {});
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setSaving(false);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Delete from form (edit mode) ─────────────────────────────────────────
  const handleDeleteFromForm = async () => {
    if (editingId === null || editingId === 'new') return;
    const mr = (allReviews || []).find(r => r.id === editingId);
    if (!mr) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'management-review', mr.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const name = mr.ref || mr.id;
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', name),
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `management-reviews/${mr.id}.json`);
            setAllReviews(prev => (prev || []).filter(r => r.id !== mr.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(mr.id); return next; });
            closeForm();
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Delete handlers ──────────────────────────────────────────────────────
  const handleDeleteOne = async (mr) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'management-review', mr.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const name = mr.ref || mr.id;
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', name),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `management-reviews/${mr.id}.json`);
            setAllReviews(prev => (prev || []).filter(r => r.id !== mr.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(mr.id); return next; });
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Derived counters ──────────────────────────────────────────────────────
  const allReviewsList = useMemo(() => allReviews || [], [allReviews]);
  const pendingActionCount = allReviewsList.reduce((sum, mr) => {
    const actions = Array.isArray(mr.actions) ? mr.actions : [];
    return sum + actions.filter(a => a.status !== 'Completed').length;
  }, 0);
  const lastReviewDate = (() => {
    if (allReviewsList.length === 0) return t.cntNever;
    const sorted = [...allReviewsList]
      .filter(mr => mr.date)
      .sort((a, b) => b.date.localeCompare(a.date));
    return sorted.length > 0 ? sorted[0].date : t.cntNever;
  })();

  // ── Filtered list ────────────────────────────────────────────────────────
  const reviews = useMemo(() => {
    return allReviewsList.filter(mr => {
      if (filterPending === 'pending') {
        const actions = Array.isArray(mr.actions) ? mr.actions : [];
        const hasPending = actions.some(a => a.status !== 'Completed');
        if (!hasPending) return false;
      }
      return true;
    });
  }, [allReviewsList, filterPending]);

  // ── Selection ────────────────────────────────────────────────────────────
  const selectedInView = reviews.filter(r => selectedIds.has(r.id));
  const allChecked     = reviews.length > 0 && selectedInView.length === reviews.length;
  const someChecked    = selectedInView.length > 0;
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(reviews.map(r => r.id)));
    else         setSelectedIds(new Set());
  };
  const toggleOne = (id, checked) => {
    setSelectedIds(prev => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };
  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(r => scanReferencesToEntity(org.id, 'management-review', r.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((r, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(r);
      else blocked.push({ record: r, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map(r => deleteOrgFile(org.id, `management-reviews/${r.id}.json`).catch(() => {})));
      const deleted = new Set(cohort.map(r => r.id));
      setAllReviews(prev => (prev || []).filter(r => !deleted.has(r.id)));
      setSelectedIds(new Set());
    };
    if (blocked.length === 0) {
      const names = items.map(r => r.ref || r.id).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  // ── Form view (separate screen, hides the list entirely) ─────────────────
  if (editingId !== null && formInitial !== null) {
    return (
      <>
        <ManagementReviewForm
          t={t}
          lang={lang}
          mode={formMode}
          mrId={editingId}
          initialValues={formInitial}
          onSave={handleFormSave}
          onCancel={handleCancel}
          onDelete={handleDeleteFromForm}
          org={org}
          saving={saving}
          saveError={saveState === 'error'}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        {/* Left: title */}
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>
            {t.title}
          </h2>
        </div>

        {/* Right: new button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleNew}
            disabled={editingId !== null}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none',
              borderRadius: 8, padding: '0.45rem 1.1rem',
              cursor: editingId ? 'not-allowed' : 'pointer',
              opacity: editingId ? 0.5 : 1,
            }}
          >
            {t.newReview}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        <SummaryCard value={allReviewsList.length} label={t.cntTotal}      color={TEXT_HEADING} />
        <SummaryCard value={pendingActionCount}    label={t.cntPending}    color={pendingActionCount > 0 ? UNSAVED_AMBER : TEXT_MUTED} />
        <SummaryCard value={lastReviewDate}        label={t.cntLastReview} color={BRAND_PRIMARY} />
      </div>

      {/* Filter bar */}
      {allReviews !== null && allReviewsList.length > 0 && editingId === null && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterPending}</span>
            <select value={filterPending} onChange={e => setFilterPending(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              <option value="pending">{t.filterHasPending}</option>
            </select>
          </div>
        </div>
      )}

      {/* Loading */}
      {allReviews === null && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </p>
      )}

      {/* Empty state */}
      {allReviews !== null && allReviewsList.length === 0 && editingId === null && (
        <div style={{
          textAlign: 'center', padding: '3rem 1.5rem',
          border: `1px dashed ${BORDER_DEFAULT}`, borderRadius: 12,
          background: BG_SURFACE,
        }}>
          <p style={{
            ...FONT_BODY_STRONG,
            color: TEXT_MUTED, margin: '0 0 0.4rem',
          }}>
            {t.emptyTitle}
          </p>
          <p style={{
            ...FONT_LABEL,
            color: TEXT_DISABLED, margin: 0,
          }}>
            {t.emptyHint}
          </p>
        </div>
      )}

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button
            onClick={handleDeleteSelected}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_TINY, borderRadius: 5,
              padding: '0.2rem 0.5rem', cursor: 'pointer',
            }}
          >
            {t.deleteSelected}
          </button>
        </div>
      )}

      {/* Reviews table */}
      {allReviews !== null && reviews.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}`, marginBottom: '1rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...LIST_TH_STYLE, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={LIST_TH_STYLE}>{t.listColRef}</th>
                <th style={LIST_TH_STYLE}>{t.listColDate}</th>
                <th style={LIST_TH_STYLE}>{t.listColPending}</th>
                <th style={LIST_TH_STYLE}>{t.listColNextMeeting}</th>
                <th style={LIST_TH_STYLE}>{t.listColActions}</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map(mr => (
                <MRRow
                  key={mr.id}
                  mr={mr}
                  lang={lang}
                  onEdit={handleEdit}
                  onView={handleView}
                  onDelete={handleDeleteOne}
                  checked={selectedIds.has(mr.id)}
                  onToggle={toggleOne}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
